import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useClassificationList } from '@/api/queries/catalog-queries'
import { useCreateInteracao } from '@/api/queries/interacao-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { usePeople } from '@/api/queries/people-queries'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardFooter,
	InspectionCardHeader,
	InspectionDateField,
	InspectionOptionGroup,
} from '@/components/inspections'
import { CardContent } from '@/components/ui/card'
import { Field, FieldContent } from '@/components/ui/field'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { ROUTES } from '@/constants/routes'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import { useInteracaoForm } from '@/hooks/forms/use-interacao-form'
import type { InteracaoFormData } from '@/schemas/interacao-schema'
import { useProfileStore } from '@/store'
import type { InteracaoAction } from '@/types/interacao'
import { getApiErrorMessage } from '@/utils/api-error'

export const InteractionPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const form = useInteracaoForm()
	const {
		setFocus,
		formState: { errors },
	} = form

	const { handleError } = useFormErrorFocus(errors, setFocus)

	const classificationId = form.watch('classificationId')

	const colors = INSPECTION_COLORS.violet

	const { defaultBranchId, defaultContractId, defaultCustomerId } =
		useProfileStore()

	const { data: branchOptions } = useOrganization()

	const { data } = useClassificationList()
	const { mutateAsync, isPending } = useCreateInteracao()
	// Context fields (persisted in zustand)
	const branchId = defaultBranchId
	const crId = defaultContractId

	const selectedBranch = branchOptions?.find((b) => b.id === branchId)
	const branchName = selectedBranch?.name ?? ''

	const { data: employeeData } = usePeople()

	// Specific fields
	const immediateActionOptions = [
		{
			value: 'CONGRATULATED',
			label: t('inspections.interaction.actionCongratulated'),
		},
		{ value: 'ORIENTED', label: t('inspections.interaction.actionOriented') },
	]

	const handleCancel = () => {
		navigate(ROUTES.INSPECTIONS.INTERACTION.INDEX)
	}

	const handleSubmit = async (formData: InteracaoFormData) => {
		if (
			!formData.date ||
			!formData.employeeId ||
			!formData.leaderId ||
			!formData.classificationId ||
			!formData.subclassificationId ||
			!formData.description ||
			!formData.action
		) {
			toast.error(t('inspections.interaction.validationError'))
			return
		}

		const selectedCr = selectedBranch?.crs?.find((c) => c.id === crId)

		// require customer when CR has customers
		if ((selectedCr?.customers?.length ?? 0) > 0 && !defaultCustomerId) {
			toast.error(
				t('inspections.common.selectCustomerRequired') ||
					'Selecione um cliente',
			)
			return
		}

		try {
			await mutateAsync({
				crId,
				...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				classificationId: formData.classificationId,
				subclassificationId: formData.subclassificationId,
				interactionDate: formData.date,
				employeeId: formData.employeeId,
				leaderId: formData.leaderId,
				description: formData.description,
				action: formData.action as InteracaoAction,
			})
			toast.success(t('inspections.interaction.saveSuccess'))
			navigate(ROUTES.INSPECTIONS.INTERACTION.INDEX)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<InspectionCard colors={colors}>
			<InspectionCardHeader
				icon={<Users className="size-5" />}
				title={t('inspections.types.interaction.title')}
				colors={colors}
				onBack={handleCancel}
				branch={branchName}
				branchId={branchId}
				branchList={branchOptions}
				crId={crId}
				crList={selectedBranch?.crs}
			/>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit, handleError)}
					className="flex flex-1 flex-col"
				>
					<CardContent className="flex flex-col gap-6 py-5">
						{/* ── Context fields ── */}
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
							<FormField
								name="date"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.common.date')}
										error={form.formState.errors.date?.message}
									>
										<InspectionDateField
											value={field.value ? new Date(field.value) : undefined}
											onChange={(date) =>
												field.onChange(date ? date.toISOString() : '')
											}
										/>
									</FormItem>
								)}
							/>

							<FormField
								name="employeeId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.interaction.employee')}
										error={form.formState.errors.employeeId?.message}
									>
										<FieldContent fieldName={field.name}>
											<SearchableSelect
												ref={field.ref}
												value={field.value}
												nameField={field.name}
												onValueChange={field.onChange}
												options={
													employeeData?.map((p) => ({
														value: p.id,
														label: p.name,
													})) ?? []
												}
												placeholder={t(
													'inspections.interaction.selectEmployee',
												)}
												searchPlaceholder={t('common.search')}
											/>
										</FieldContent>
									</FormItem>
								)}
							/>

							<FormField
								name="leaderId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.interaction.leader')}
										error={form.formState.errors.leaderId?.message}
									>
										<FieldContent fieldName={field.name}>
											<SearchableSelect
												ref={field.ref}
												value={field.value}
												nameField={field.name}
												onValueChange={field.onChange}
												options={
													employeeData?.map((p) => ({
														value: p.id,
														label: p.name,
													})) ?? []
												}
												placeholder={t('inspections.interaction.selectLeader')}
												searchPlaceholder={t('common.search')}
											/>
										</FieldContent>
									</FormItem>
								)}
							/>
						</div>

						<Separator className="bg-border/60" />

						<FormField
							name="classificationId"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.interaction.classification')}
									error={form.formState.errors.classificationId?.message}
								>
									<FieldContent fieldName={field.name}>
										<NativeSelect
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={(e) => {
												field.onChange(e)
												form.setValue('subclassificationId', '')
											}}
											className="w-full min-w-40 truncate"
										>
											<NativeSelectOption value="" disabled>
												{t('inspections.interaction.selectClassification')}
											</NativeSelectOption>
											{data?.items.map((item) => (
												<NativeSelectOption
													key={item.id}
													value={item.id}
													disabled={
														(item.subclassifications ?? []).length === 0
													}
												>
													{item.name}
												</NativeSelectOption>
											))}
										</NativeSelect>
									</FieldContent>
								</FormItem>
							)}
						/>

						{classificationId && (
							<FormField
								name="subclassificationId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.interaction.subclassification')}
										error={form.formState.errors.subclassificationId?.message}
									>
										<Field className="animate-in fade-in slide-in-from-top-2 duration-200">
											<FieldContent fieldName={field.name}>
												<NativeSelect
													name={field.name}
													ref={field.ref}
													value={field.value}
													onChange={field.onChange}
													className="w-full"
												>
													<NativeSelectOption value="" disabled>
														{t(
															'inspections.interaction.selectSubclassification',
														)}
													</NativeSelectOption>
													{data?.items
														.find((item) => item.id === classificationId)
														?.subclassifications.map((item) => (
															<NativeSelectOption key={item.id} value={item.id}>
																{item.name}
															</NativeSelectOption>
														)) || []}
												</NativeSelect>
											</FieldContent>
										</Field>
									</FormItem>
								)}
							/>
						)}

						<FormField
							name="description"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.interaction.description')}
									error={form.formState.errors.description?.message}
								>
									<FieldContent fieldName={field.name}>
										<Textarea
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
											placeholder={t(
												'inspections.interaction.descriptionPlaceholder',
											)}
											rows={4}
											className="resize-y"
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<Separator className="bg-border/60" />

						<FormField
							name="action"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.interaction.immediateAction')}
									error={form.formState.errors.action?.message}
								>
									<FieldContent fieldName={field.name}>
										<InspectionOptionGroup
											value={field.value}
											onChange={field.onChange}
											options={immediateActionOptions}
											activeClass={colors.optionActive}
											columns={2}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>
					</CardContent>

					<InspectionCardFooter
						colors={colors}
						isSubmitting={isPending}
						onCancel={handleCancel}
						cancelLabel={t('inspections.interaction.cancel')}
						saveLabel={t('inspections.interaction.save')}
						savingLabel={t('inspections.interaction.saving')}
					/>
				</form>
			</Form>
		</InspectionCard>
	)
}
